import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AgentConfig, QueryType, PersonaConfig, CallEventAdapter } from "../types";
import { useSession } from "../context/SessionContext";
import { useCallEvents } from "../hooks/useCallEvents";
import { useCallTimer } from "../hooks/useCallTimer";
import { NuplayCallAdapter, agentIdForQuery } from "../adapters/nurixAdapter";

// ─────────────────────────────────────────────────────────────────────────────
// Copa Airlines call dashboard — minimalist build of the provided mockup:
//   header (live + title + passenger profile card) · conversation (bubbles + mic)
//   · Agent Activity timeline (tool callouts) · Live Workflow (4-agent crew).
// Fed by live session state (transcript, liveActions, livePassenger, agentPath).
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  agent: AgentConfig;
  queryType: QueryType;
  persona: PersonaConfig;
}

const STATUS_TEXT: Record<string, string> = {
  idle: "Ready", setting_up: "Connecting", connecting: "Connecting",
  in_call: "Live", ended: "Call ended", failed: "Something went wrong",
};

// ── Minimal line icons ───────────────────────────────────────────────────────
const sv = { fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
const Ic = ({ d, s = 16, fill = false }: { d: string; s?: number; fill?: boolean }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" {...(fill ? { fill: "currentColor" } : sv)}>
    {d.split("|").map((p, i) => <path key={i} d={p} />)}
  </svg>
);
const IcRoute = () => <Ic d="M6 3v12a3 3 0 0 0 3 3h6|M15 15l3 3-3 3|M18 3a3 3 0 1 1-6 0 3 3 0 0 1 6 0" />;
const IcRefund = () => <Ic d="M3 9a9 9 0 1 1-1.6 5.2|M3 4v5h5" />;
const IcBag = () => <Ic d="M4 8h16a1 1 0 0 1 1 1v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a1 1 0 0 1 1-1z|M8 8V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3" />;
const IcHeadset = () => <Ic d="M4 14v-2a8 8 0 0 1 16 0v2|M4 14a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2z|M20 14a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2z" />;
const IcMic = ({ s = 22 }: { s?: number }) => <Ic s={s} d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z|M19 11a7 7 0 0 1-14 0|M12 18v3" />;
const IcX = ({ s = 16 }: { s?: number }) => <Ic s={s} d="M18 6 6 18|M6 6l12 12" />;
const IcChat = () => <Ic s={17} d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />;
const IcActivity = () => <Ic s={16} d="M22 12h-4l-3 8L9 4l-3 8H2" />;
const IcSpark = () => <Ic s={14} fill d="M12 2l1.5 5.2L18.5 9l-5 1.8L12 16l-1.5-5.2L5.5 9l5-1.8z" />;

// Agent avatar — uses /copa-agent.png (drop the image into public/); falls back
// to the sparkle glyph if the file isn't present yet.
function AgentAvatar() {
  const [imgErr, setImgErr] = useState(false);
  if (imgErr) return <IcSpark />;
  return <img className="cd-voice-img" src="/copa-agent.png" alt="" onError={() => setImgErr(true)} />;
}

// The 4-agent crew, in routing order — used for the Live Workflow strip.
const CREW_FLOW = [
  { key: "routing", label: "Routing", match: /rout/i, Icon: IcRoute },
  { key: "refund", label: "Refund", match: /refund|rebook/i, Icon: IcRefund },
  { key: "baggage", label: "Baggage", match: /baggage|voucher/i, Icon: IcBag },
  { key: "human", label: "Human", match: /human|transfer|specialist/i, Icon: IcHeadset },
];

function humanizeFn(name: string): string {
  const map: Record<string, string> = {
    get_booking: "Look up booking",
    get_baggage: "Look up baggage claim",
    get_reimbursement: "Check reimbursement",
    get_voucher: "Check voucher",
    transfer_tool: "Transfer to specialist",
  };
  if (map[name]) return map[name];
  return (name || "Tool call").replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function shortVal(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    const first = Object.entries(o)[0];
    return first ? `${first[0]}: ${String(first[1])}` : JSON.stringify(o).slice(0, 40);
  }
  return String(v);
}

// Lightweight live sentiment from the caller's own turns (heuristic, no extra calls).
function computeSentiment(transcript: { speaker: string; text: string }[]) {
  const NEG = /\b(sorr|frustrat|angr|ridiculous|unacceptable|upset|disappoint|complaint|lost|delay|weeks|never|wrong|terrible|annoy|died|passed away|death|loss|bereave|emergenc|sue|lawyer|chargeback|dispute|haven'?t (received|got)|still (haven|not))\b/i;
  const POS = /\b(thank|appreciat|great|perfect|helpful|wonderful|relieved|reassur|that works|sounds good|yes please|okay great|glad|amazing)\b/i;
  let pos = 0, neg = 0, neu = 0;
  for (const t of transcript) {
    if (t.speaker !== "user") continue;
    const isNeg = NEG.test(t.text), isPos = POS.test(t.text);
    if (isNeg && !isPos) neg++; else if (isPos && !isNeg) pos++; else neu++;
  }
  const total = pos + neg + neu;
  if (!total) return { pos: 0, neu: 100, neg: 0, label: "Awaiting", live: false };
  const P = Math.round((pos / total) * 100), N = Math.round((neg / total) * 100);
  const U = Math.max(0, 100 - P - N);
  let label = "Neutral";
  if (N >= 50) label = "Distressed";
  else if (N > P) label = "Concerned";
  else if (P >= 50) label = "Reassured";
  else if (P > 0) label = "Positive";
  return { pos: P, neu: U, neg: N, label, live: true };
}

export function CopaCallSurfaceStep({ agent, queryType, persona }: Props) {
  const navigate = useNavigate();
  const { state, dispatch } = useSession();
  const adapterRef = useRef<CallEventAdapter | null>(null);
  const [callId] = useState(() => `call-${Date.now()}`);
  const [adapterInstance, setAdapterInstance] = useState<CallEventAdapter | null>(null);
  const [waveTick, setWaveTick] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);

  const timerDisplay = useCallTimer(state.startedAt, state.endedAt, state.callState === "in_call");

  const startAdapter = useCallback((adapter: CallEventAdapter) => {
    adapterRef.current = adapter;
    setAdapterInstance(adapter);
    dispatch({ type: "CALL_STATE_CHANGE", state: "setting_up" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Adapter lifecycle (mirrors CallSurfaceStep). Copa is web-only.
  useEffect(() => {
    const teardown = () => {
      adapterRef.current?.disconnect();
      adapterRef.current = null;
      dispatch({ type: "RESET" });
    };
    startAdapter(new NuplayCallAdapter({
      queryType,
      channel: "web",
      agentId: agent.agentId ?? agentIdForQuery(queryType),
      workspaceId: agent.workspaceId,
      apiBase: agent.apiBase,
      wsBase: agent.wsBase,
      agentNameOverrides: agent.agentNameOverrides,
    }));
    return teardown;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state.callState !== "in_call") return;
    const id = setInterval(() => setWaveTick((t) => t + 1), 200);
    return () => clearInterval(id);
  }, [state.callState]);

  useEffect(() => {
    const endNow = () => { adapterRef.current?.disconnect(); adapterRef.current = null; dispatch({ type: "CALL_STATE_CHANGE", state: "ended" }); };
    window.addEventListener("pagehide", endNow);
    return () => window.removeEventListener("pagehide", endNow);
  }, [dispatch]);

  useCallEvents(adapterInstance, callId);

  const handleEnd = useCallback(() => { adapterRef.current?.disconnect(); dispatch({ type: "CALL_STATE_CHANGE", state: "ended" }); }, [dispatch]);
  const handleMute = useCallback(() => { const next = !state.isMuted; dispatch({ type: "TOGGLE_MUTE" }); (adapterRef.current as NuplayCallAdapter)?.setMuted?.(next); }, [dispatch, state.isMuted]);
  const handleClose = () => { handleEnd(); navigate("/nurix-experience/copa"); };

  const isActive = state.callState === "in_call";
  const isEnded = state.callState === "ended" || state.callState === "failed";
  const p = state.livePassenger;

  // ── Profile card fields ──
  const passengerName = p && (p.firstName || p.lastName) ? [p.firstName, p.lastName].filter(Boolean).join(" ") : persona.name;
  const bookingRef = p?.bookingRef ?? persona.accountId;
  const tier = p?.loyaltyTier ?? persona.loyaltyTier;
  const verified = Boolean(p);
  const money = p?.refundAmount ? (p.refundAmount.includes("$") || !p.currency ? p.refundAmount : `${p.refundAmount} ${p.currency}`) : undefined;
  const detail: { label: string; value?: string; sub?: string }[] = p ? [
    { label: "Route", value: p.route, sub: [p.origin, p.destination].filter(Boolean).join(" → ") || undefined },
    { label: "Flight", value: p.flightNumber, sub: [p.travelDate, p.fareType].filter(Boolean).join(" · ") || undefined },
    { label: "Email", value: p.email },
    { label: "Language", value: p.preferredLanguage },
    { label: "Booking", value: p.bookingRef, sub: [p.bookingStatus, p.ticketStatus].filter(Boolean).join(" · ") || undefined },
    { label: "Refund case", value: p.refundCaseId, sub: [p.refundStatus, p.refundStage].filter(Boolean).join(" · ") || undefined },
    { label: "Refund", value: money, sub: p.expectedSla ? `ETA ${p.expectedSla}` : undefined },
    { label: "Voucher", value: p.voucherId, sub: p.voucherEligible ? `eligible: ${p.voucherEligible}` : undefined },
    { label: "Baggage", value: p.baggageStatus, sub: p.reimbursementStatus ? `reimbursement: ${p.reimbursementStatus}` : undefined },
    { label: "Handling", value: p.specialHandling },
    { label: "Note", value: p.policyNote },
  ].filter((d) => d.value) : [];

  // ── Agent Activity events (from liveActions + verification) ──
  type Ev = { id: string; kind: "verified" | "tool" | "handoff"; title: string; desc: string; ok: boolean; tool?: { fn: string; input: string; ms: number; isError: boolean } };
  const events: Ev[] = [];
  if (verified) events.push({ id: "verified", kind: "verified", title: "Passenger verified", desc: `${passengerName} · ${bookingRef}`, ok: true });
  for (const a of state.liveActions) {
    if (a.type === "tool_call") {
      events.push({
        id: a.id, kind: "tool", ok: !a.isError,
        title: humanizeFn(a.functionName),
        desc: a.isError ? "Lookup error" : "Completed",
        tool: { fn: a.functionName, input: shortVal(Object.values(a.args ?? {})[0] ?? a.args), ms: a.durationMs, isError: a.isError },
      });
    } else if (a.type === "agent_handoff") {
      events.push({ id: a.id, kind: "handoff", ok: true, title: `Routed to ${a.toAgent}`, desc: a.routingCondition || "Agent handoff" });
    }
  }

  const sentiment = computeSentiment(state.transcript);

  // ── Live Workflow active node (from agentPath) ──
  const activeAgentName = state.agentPath[state.agentPath.length - 1]?.agent ?? "";
  let activeFlowIdx = CREW_FLOW.findIndex((f) => f.match.test(activeAgentName));
  if (activeFlowIdx < 0) activeFlowIdx = isActive || verified ? 0 : -1; // default to Routing once connected

  return (
    <div className="copa-dash">
      <style>{COPA_CSS}</style>

      <main className="cd-main">
        {/* Header */}
        <header className="cd-header">
          <div>
            <div className={`cd-live ${isActive ? "" : "cd-live-idle"}`}><span className="cd-dot" />{isActive ? "Live" : STATUS_TEXT[state.callState]}</div>
            <h1 className="cd-title">{agent.name}</h1>
            <div className="cd-meta">
              <span>Copa Airlines · Passenger Care</span>
              {(isActive || isEnded) && <span className="cd-timer">{timerDisplay}</span>}
            </div>
          </div>

          {/* Passenger profile card (click to expand) */}
          <div className="cd-profile-wrap">
            <div className="cd-profile-card" onClick={() => setProfileOpen((o) => !o)} role="button" tabIndex={0}>
              <div className={`cd-avatar bg-gradient-to-br ${persona.avatarBg}`}>{persona.avatarInitials}</div>
              <div className="cd-profile">
                <h3>{passengerName}<span className="cd-tag">{tier}</span></h3>
                <p>{verified ? `Booking ${bookingRef}` : `${bookingRef} · awaiting verification`}</p>
                <div className="cd-profile-foot">
                  <strong>{p?.route ?? "Copa Airlines"}</strong>
                  <span className="cd-status">{p?.refundStatus ?? p?.bookingStatus ?? "—"}</span>
                </div>
              </div>
              <svg className={`cd-chev ${profileOpen ? "open" : ""}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
            </div>
            {profileOpen && (
              <div className="cd-profile-drop">
                {detail.length > 0 ? (
                  <div className="cd-drop-grid">
                    {detail.map((d) => (
                      <div key={d.label} className="cd-drop-item">
                        <span className="cd-drop-label">{d.label}</span>
                        <span className="cd-drop-val" title={d.value}>{d.value}</span>
                        {d.sub && <span className="cd-drop-sub" title={d.sub}>{d.sub}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="cd-drop-empty">The passenger's full details — route, flight, refund and baggage — appear here once verified on the call.</div>
                )}
              </div>
            )}
          </div>
        </header>

        <section className="cd-content">
          {/* Conversation */}
          <div className="cd-panel cd-conversation">
            <div className="cd-panel-title"><IcChat /> Conversation</div>

            <div className="cd-bubbles">
              {state.transcript.length === 0 ? (
                <div className="cd-empty">
                  <div className="cd-mic-empty"><IcMic s={20} /></div>
                  <p>{isActive ? "Listening…" : STATUS_TEXT[state.callState]}</p>
                </div>
              ) : (
                state.transcript.map((t) => {
                  if (t.speaker === "user") {
                    return (
                      <div key={t.id} className="cd-bubble-row user">
                        <div><div className="cd-speaker">You</div><div className="cd-bubble cd-bubble-user">{t.text}</div></div>
                      </div>
                    );
                  }
                  if (t.speaker === "tool" || t.speaker === "system") return null;
                  return (
                    <div key={t.id} className="cd-bubble-row">
                      <div className="cd-agent-wrap">
                        <div className="cd-voice-dot"><AgentAvatar /></div>
                        <div><div className="cd-speaker">{agent.personaName}</div><div className="cd-bubble cd-bubble-agent">{t.text}</div></div>
                      </div>
                    </div>
                  );
                })
              )}
              {isActive && state.transcript[state.transcript.length - 1]?.speaker === "user" && (
                <div className="cd-bubble-row">
                  <div className="cd-agent-wrap">
                    <div className="cd-voice-dot"><AgentAvatar /></div>
                    <div className="cd-thinking"><span></span><span></span><span></span></div>
                  </div>
                </div>
              )}
            </div>

            {/* Voice controls */}
            <div className="cd-voice-controls">
              <div key={waveTick} className="cd-wave">
                {Array.from({ length: 26 }, (_, i) => (
                  <span key={i} style={{ height: `${isActive ? 3 + Math.abs(Math.sin(i * 0.7 + waveTick * 0.4)) * 16 : 3}px` }} />
                ))}
              </div>
              <div className="cd-controls-row">
                {!isEnded ? (
                  <>
                    <button className="cd-end" onClick={handleEnd} aria-label="End call"><IcX /></button>
                    <button className={`cd-mic ${state.isMuted ? "muted" : ""}`} onClick={handleMute} aria-label={state.isMuted ? "Unmute" : "Mute"}><IcMic /></button>
                  </>
                ) : (
                  <button className="cd-restart" onClick={() => navigate("/nurix-experience/copa")}>Try another scenario</button>
                )}
              </div>
            </div>
          </div>

          {/* Right panel */}
          <div className="cd-right">
            {/* Live sentiment */}
            <div className="cd-panel cd-sentiment">
              <div className="cd-sent-head">
                <span className="cd-sent-title">Caller Sentiment</span>
                <span className={`cd-sent-label cd-sent-${sentiment.label.toLowerCase()}`}>{sentiment.label}</span>
              </div>
              <div className="cd-sent-bar">
                <span className="pos" style={{ width: `${sentiment.pos}%` }} />
                <span className="neu" style={{ width: `${sentiment.neu}%` }} />
                <span className="neg" style={{ width: `${sentiment.neg}%` }} />
              </div>
              <div className="cd-sent-legend">
                <span><i className="d-pos" />Positive {sentiment.pos}%</span>
                <span><i className="d-neu" />Neutral {sentiment.neu}%</span>
                <span><i className="d-neg" />Concern {sentiment.neg}%</span>
              </div>
            </div>

            <div className="cd-panel cd-activity">
              <div className="cd-panel-title"><IcActivity /> Agent Activity</div>
              {events.length === 0 ? (
                <div className="cd-activity-empty">Agent decisions and data lookups will appear here during the call.</div>
              ) : (
                <div className="cd-timeline">
                  {events.map((e) => (
                    <div className="cd-event" key={e.id}>
                      <span className={`cd-node ${e.ok ? "success" : "error"}`} />
                      <div className="cd-event-main">
                        <h4>{e.title}</h4>
                        <p>{e.desc}</p>
                      </div>
                      {e.kind === "verified" ? <div className="cd-pill">verified</div> : <div className="cd-ok">{e.tool ? `${e.tool.ms}ms` : ""}</div>}
                      {e.tool && (
                        <div className="cd-tool-callout">
                          <div><small>Tool</small><strong>{e.tool.fn}</strong></div>
                          <div><small>Input</small><strong>{e.tool.input}</strong></div>
                          <div><small>Status</small><strong style={{ color: e.tool.isError ? "#ef4444" : "#16a34a" }}>{e.tool.isError ? "error" : "ok"}</strong></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Live Workflow (crew) */}
            <div className="cd-panel cd-workflow">
              <div className="cd-workflow-head">Live Workflow <span className="cd-live cd-live-rt"><span className="cd-dot" />Real-time</span></div>
              <div className="cd-flow">
                {CREW_FLOW.map((f, i) => {
                  const live = i === activeFlowIdx;
                  return (
                    <div key={f.key} className={`cd-flow-card ${live ? "active" : "muted"}`}>
                      <div className="cd-flow-icon"><f.Icon /></div>
                      <span className="cd-flow-label">
                        {live && <span className="cd-flow-dot" />}
                        {f.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </main>

      <button className="cd-close-x" onClick={handleClose} aria-label="Exit"><IcX s={15} /></button>
    </div>
  );
}

const COPA_CSS = `
.copa-dash { --acc:#2563eb; --soft:#eff4ff; --ink:#0f172a; --muted:#64748b; --faint:#94a3b8; --line:#edf0f5; --card:#ffffff; --bg:#f7f9fc; position:fixed; inset:0; display:grid; grid-template-columns:1fr; background:var(--bg); color:var(--ink); font-family:Nunito,system-ui,sans-serif; overflow:hidden; z-index:50; -webkit-font-smoothing:antialiased; }
.copa-dash * { box-sizing:border-box; }
.cd-main { padding:30px 34px; overflow:hidden; display:flex; flex-direction:column; }
.cd-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:26px; }
.cd-live { display:inline-flex; align-items:center; gap:6px; color:#16a34a; background:#f0fdf4; padding:4px 11px; border-radius:999px; font-size:11px; font-weight:600; letter-spacing:.01em; margin-bottom:14px; }
.cd-live .cd-dot { width:6px; height:6px; border-radius:50%; background:currentColor; animation:cdpulse 1.6s infinite ease-in-out; }
.cd-live-idle { color:var(--muted); background:#f1f5f9; }
.cd-live-rt { margin:0; }
.cd-title { font-family:Manrope,sans-serif; font-size:23px; font-weight:700; letter-spacing:-0.02em; margin-bottom:8px; }
.cd-meta { display:flex; gap:14px; color:var(--faint); font-size:13px; align-items:center; }
.cd-timer { font-variant-numeric:tabular-nums; padding:2px 9px; background:#f1f5f9; border-radius:999px; color:var(--muted); font-weight:500; }
.cd-profile-card { display:flex; align-items:center; gap:14px; background:var(--card); border:1px solid var(--line); border-radius:16px; padding:14px 18px; min-width:330px; }
.cd-avatar { width:44px; height:44px; border-radius:50%; display:grid; place-items:center; font-size:13px; font-weight:700; color:#fff; flex-shrink:0; }
.cd-profile h3 { font-size:15px; font-weight:600; margin-bottom:4px; display:flex; align-items:center; gap:8px; }
.cd-profile p { font-size:12px; color:var(--faint); margin-bottom:7px; }
.cd-profile-foot { display:flex; align-items:center; gap:10px; }
.cd-profile-foot strong { font-size:12px; font-weight:600; color:var(--ink); }
.cd-tag { background:var(--soft); color:var(--acc); padding:2px 8px; border-radius:999px; font-size:10px; font-weight:600; }
.cd-status { color:var(--acc); font-weight:500; font-size:12px; }
.cd-profile-wrap { position:relative; }
.cd-profile-card { cursor:pointer; user-select:none; }
.cd-chev { color:var(--faint); transition:transform .2s ease; flex-shrink:0; align-self:center; margin-left:auto; }
.cd-chev.open { transform:rotate(180deg); }
.cd-profile-drop { position:absolute; top:calc(100% + 8px); right:0; width:360px; background:var(--card); border:1px solid var(--line); border-radius:16px; box-shadow:0 18px 44px rgba(16,24,40,.14); padding:16px 18px; z-index:5; }
.cd-drop-grid { display:grid; grid-template-columns:1fr 1fr; gap:13px 18px; }
.cd-drop-item { display:flex; flex-direction:column; gap:2px; min-width:0; }
.cd-drop-label { font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:var(--faint); font-weight:600; }
.cd-drop-val { font-size:13px; color:var(--ink); font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.cd-drop-sub { font-size:11px; color:var(--muted); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.cd-drop-empty { font-size:12px; color:var(--muted); line-height:1.55; }
.cd-content { display:grid; grid-template-columns:52% 48%; gap:20px; flex:1; min-height:0; }
.cd-panel { background:var(--card); border:1px solid var(--line); border-radius:20px; box-shadow:0 1px 2px rgba(16,24,40,.03); }
.cd-conversation { padding:26px; display:flex; flex-direction:column; min-height:0; }
.cd-panel-title { display:flex; align-items:center; gap:9px; font-family:Manrope,sans-serif; font-size:15px; font-weight:600; letter-spacing:-0.01em; margin-bottom:22px; color:var(--ink); }
.cd-panel-title svg { color:var(--faint); }
.cd-bubbles { flex:1; overflow-y:auto; padding-right:4px; }
.cd-empty { height:100%; display:flex; flex-direction:column; align-items:center; justify-content:center; color:var(--faint); gap:14px; font-size:13px; }
.cd-mic-empty { width:46px; height:46px; border-radius:50%; background:#f1f5f9; display:grid; place-items:center; color:var(--faint); }
.cd-bubble-row { display:flex; margin-bottom:18px; }
.cd-bubble-row.user { justify-content:flex-end; }
.cd-speaker { font-size:10px; color:var(--faint); margin-bottom:6px; font-weight:600; letter-spacing:.04em; text-transform:uppercase; }
.cd-bubble-row.user .cd-speaker { text-align:right; }
.cd-bubble { max-width:380px; padding:12px 16px; border-radius:16px; font-size:14px; line-height:1.55; }
.cd-bubble-agent { background:var(--soft); color:#1e293b; border-bottom-left-radius:5px; }
.cd-bubble-user { background:#f1f5f9; color:#1e293b; border-bottom-right-radius:5px; }
.cd-agent-wrap { display:flex; gap:11px; align-items:flex-start; }
.cd-voice-dot { width:34px; height:34px; border-radius:50%; background:var(--soft); color:var(--acc); display:grid; place-items:center; flex-shrink:0; margin-top:18px; overflow:hidden; }
.cd-voice-img { width:100%; height:100%; object-fit:cover; border-radius:50%; }
.cd-thinking { padding:13px 16px; border-radius:16px; border-bottom-left-radius:5px; background:var(--soft); display:flex; gap:6px; align-self:flex-end; }
.cd-thinking span { width:6px; height:6px; background:var(--acc); border-radius:50%; animation:cdpulse 1.2s infinite ease-in-out; }
.cd-thinking span:nth-child(2){animation-delay:.15s;} .cd-thinking span:nth-child(3){animation-delay:.3s;}
@keyframes cdpulse { 0%,100%{opacity:.3;transform:translateY(0);} 50%{opacity:1;transform:translateY(-3px);} }
.cd-voice-controls { margin-top:16px; }
.cd-wave { display:flex; align-items:center; justify-content:center; gap:3px; height:24px; margin-bottom:14px; }
.cd-wave span { width:3px; border-radius:3px; background:rgba(37,99,235,.4); transition:height .2s ease; }
.cd-controls-row { display:flex; align-items:center; justify-content:center; gap:16px; }
.cd-mic { width:56px; height:56px; border-radius:50%; border:none; background:var(--acc); color:#fff; display:grid; place-items:center; box-shadow:0 6px 18px rgba(37,99,235,.28); cursor:pointer; transition:transform .12s; }
.cd-mic:active { transform:scale(.95); }
.cd-mic.muted { background:#e2e8f0; color:var(--muted); box-shadow:none; }
.cd-end { width:44px; height:44px; border-radius:50%; border:1px solid #fde2e2; background:#fef2f2; color:#ef4444; display:grid; place-items:center; cursor:pointer; }
.cd-restart { padding:11px 22px; border-radius:12px; border:1px solid var(--line); background:var(--card); font-size:13px; font-weight:500; color:var(--ink); cursor:pointer; }
.cd-right { display:flex; flex-direction:column; gap:20px; overflow-y:auto; padding:1px; }
.cd-sentiment { padding:18px 20px; }
.cd-sent-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:13px; }
.cd-sent-title { font-family:Manrope,sans-serif; font-size:14px; font-weight:600; }
.cd-sent-label { font-size:11px; font-weight:600; padding:3px 10px; border-radius:999px; background:#f1f5f9; color:var(--muted); }
.cd-sent-distressed, .cd-sent-concerned { background:#fef2f2; color:#dc2626; }
.cd-sent-reassured, .cd-sent-positive { background:#f0fdf4; color:#16a34a; }
.cd-sent-bar { display:flex; height:8px; border-radius:999px; overflow:hidden; background:#f1f5f9; }
.cd-sent-bar span { transition:width .4s ease; }
.cd-sent-bar .pos { background:#22c55e; } .cd-sent-bar .neu { background:#cbd5e1; } .cd-sent-bar .neg { background:#f59e0b; }
.cd-sent-legend { display:flex; gap:14px; margin-top:11px; font-size:11px; color:var(--muted); }
.cd-sent-legend i { display:inline-block; width:7px; height:7px; border-radius:50%; margin-right:5px; }
.cd-sent-legend .d-pos { background:#22c55e; } .cd-sent-legend .d-neu { background:#cbd5e1; } .cd-sent-legend .d-neg { background:#f59e0b; }
.cd-activity { flex:1; padding:26px; }
.cd-activity-empty { color:var(--faint); font-size:13px; padding:30px 4px; text-align:center; }
.cd-timeline { position:relative; padding-left:28px; }
.cd-timeline::before { content:""; position:absolute; left:6px; top:6px; bottom:14px; width:1.5px; background:var(--line); }
.cd-event { position:relative; display:grid; grid-template-columns:1fr auto; gap:8px 12px; padding:14px 0; border-bottom:1px solid var(--line); }
.cd-event:last-child { border-bottom:none; padding-bottom:2px; }
.cd-node { position:absolute; left:-28px; top:17px; width:13px; height:13px; border-radius:50%; background:#fff; border:2px solid var(--acc); }
.cd-node.success { background:#22c55e; border-color:#22c55e; }
.cd-node.error { background:#ef4444; border-color:#ef4444; }
.cd-event-main h4 { font-size:13px; font-weight:600; margin-bottom:3px; }
.cd-event-main p { color:var(--muted); font-size:12px; }
.cd-pill { align-self:start; background:#f0fdf4; color:#16a34a; padding:3px 9px; border-radius:999px; font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.03em; }
.cd-ok { align-self:start; color:var(--faint); font-size:11px; font-variant-numeric:tabular-nums; }
.cd-tool-callout { grid-column:1 / 3; display:grid; grid-template-columns:1.2fr 1.4fr auto; gap:14px; padding:11px 14px; margin-top:8px; border:1px solid var(--line); border-radius:12px; background:var(--bg); }
.cd-tool-callout small { display:block; color:var(--faint); margin-bottom:4px; font-size:10px; text-transform:uppercase; letter-spacing:.05em; }
.cd-tool-callout strong { font-size:12px; font-weight:600; word-break:break-all; }
.cd-workflow { padding:20px 22px; }
.cd-workflow-head { display:flex; gap:10px; align-items:center; margin-bottom:16px; font-family:Manrope,sans-serif; font-weight:600; font-size:14px; }
.cd-flow { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; }
.cd-flow-card { border:1px solid var(--line); border-radius:14px; padding:13px; min-height:62px; display:flex; flex-direction:column; align-items:flex-start; gap:10px; font-size:12px; font-weight:500; background:var(--card); transition:opacity .25s ease, border-color .2s ease; }
.cd-flow-card.active { border-color:var(--acc); box-shadow:0 0 0 3px rgba(37,99,235,.07); color:var(--acc); }
.cd-flow-card.active .cd-flow-icon { background:var(--acc); color:#fff; }
.cd-flow-card.muted { opacity:.4; }
.cd-flow-label { display:inline-flex; align-items:center; gap:6px; }
.cd-flow-dot { width:6px; height:6px; border-radius:50%; background:var(--acc); animation:cdpulse 1.2s infinite ease-in-out; flex-shrink:0; }
.cd-flow-icon { width:32px; height:32px; border-radius:10px; background:var(--soft); color:var(--acc); display:grid; place-items:center; transition:background .2s ease, color .2s ease; }
.cd-close-x { position:fixed; top:20px; left:20px; z-index:60; width:32px; height:32px; border-radius:50%; border:1px solid var(--line); background:var(--card); color:var(--muted); display:grid; place-items:center; cursor:pointer; }
.cd-close-x:hover { color:var(--ink); }
`;
